// Lib
import React, { useEffect } from 'react';
import { getFloatingPanelMountPoint } from '../../../components/tooltips/floatingPanelUtils';
import { createPortal } from 'react-dom';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import ModalCloseButton from '../../../components/modal/ModalCloseButton';
import { getFileUrl } from '../../../../common/elements/utils/elementPropertyUtils';
import { stopDefaultAndPropagation } from '../../../utils/domUtil';

// Styles
import './IPhoneVideoPlayerModal.scss';

interface IphoneVideoPlayerModalProps {
    element: ImMNElement;
    showDownloadOption: boolean;
    setShowVideoPlayerModal: (show: boolean) => void;
}

/**
 * Renders a video player within a modal.
 * This is used for iPhones, where the video player will automatically create a new composition layer,
 * which can cause crashes when that layer is on the canvas as it causes all other elements to end up on a layer too.
 *
 * When it exists in a modal, the canvas elements won't be on new layers & thus the crash can be avoided.
 */
const IPhoneVideoPlayerModal: React.FC<IphoneVideoPlayerModalProps> = (props) => {
    const { element, setShowVideoPlayerModal, showDownloadOption } = props;
    const videoPlayerRef = React.useRef<HTMLVideoElement>(null);

    const url = getFileUrl(element);

    const portalMountPoint = getFloatingPanelMountPoint();

    /**
     * On iPhones, when the video player starts playing, via autoplay - it will be
     * automatically put into fullscreen mode.
     * When we exit fullscreen mode, we want to close the modal as well.
     */
    useEffect(() => {
        if (!videoPlayerRef.current) return;

        const abortController = new AbortController();

        videoPlayerRef.current.addEventListener(
            'webkitendfullscreen',
            () => {
                setShowVideoPlayerModal(false);
            },
            { signal: abortController.signal },
        );

        return () => {
            abortController.abort();
        };
    }, []);

    const onClose = React.useCallback(() => {
        setShowVideoPlayerModal(false);
    }, []);

    return createPortal(
        <div
            className="IPhoneVideoPlayerModal"
            onClick={stopDefaultAndPropagation}
            onMouseDown={stopDefaultAndPropagation}
        >
            <ModalCloseButton close={onClose} />
            <video
                ref={videoPlayerRef}
                className="video-player"
                controls
                autoPlay
                controlsList={!showDownloadOption ? 'nodownload' : ''}
                src={url}
            />
        </div>,
        portalMountPoint,
    );
};

export default IPhoneVideoPlayerModal;
